import { Box, Grid, IconButton, styled, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getFeatureDetails, getFeaturesListByTenant } from "../../features/featuresDetails/featureDetailsAction";
import moment from "moment";
import EnterpriseTableComponent, { ActionCell } from "./FeaturesTable";
import { IconCaretDown, IconCaretRight, IconTrash } from "@tabler/icons-react";
import request from "../../request/request";
import FormTextField from "../Common/Forms/FormTextField";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import Dialog from "../Common/Dialog";

export const FormContent = styled(Grid)(({ theme }) => ({
  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
  },
}));
const ExpandCell = ({ expandedRows, handleToggleExpand, row }) => {
  const isExpanded = expandedRows[row.original.uuid];
  return (
    <IconButton onClick={() => handleToggleExpand(row)}>
      {isExpanded ? <IconCaretDown /> : <IconCaretRight />}
    </IconButton>
  );
};

const Features = ({
  hideFeatureButton,
  hideEditIcon,
  hideDeleteIcon,
  hideAddIcon,
  showToggle,
  tenantId,
}) => {
  const validationSchemaAddFeature = Yup.object({
    name: Yup.string()
      .max(255, "Please Enter a valid name")
      .required("Name is required!"),
    description: Yup.string()
      .max(255, "Please Enter a valid Description")
      .required("Description is required!"),
  });

  const [expandedRows, setExpandedRows] = useState({});
  const [expandedRowsData, setExpandedRowsData] = useState([]);
  const [openAddFeatureDialog, setOpenAddFeatureDialog] = useState(false);
  const [openAddSubFeatureDialog, setOpenAddSubFeatureDialog] = useState(false);

  const [subRow, setSubRow] = useState({});
  const [subFeatRow, setSubFeatRow] = useState({});
  const [editUserDialogIsOpen, setEditUserDialogIsOpen] = useState(false);

  const handleEditFeatureDialog = async (rowObj) => {
    setSubRow(rowObj);
    setOpenAddFeatureDialog(true);
  };

  const handleEditSubFeatureDialog = async (rowObj) => {
    setSubFeatRow(rowObj);
    setOpenAddSubFeatureDialog(true);
  };
  const handleAddSubFeatureDialog = async (rowObj) => {
    setSubRow(rowObj);
    setOpenAddSubFeatureDialog(true);
  };

  const columnsExpandableTable = [
    {
      Header: " ",
    },
    {
      accessor: "name",
      Header: "Name",
    },
    {
      accessor: "description",
      Header: "Description",
    },
    {
      accessor: "createdOn",
      Header: "Created On",
    },
    {
      Header: "Action",
      Cell: ({ row }) => (
        <ActionCell
          row={row}
          hideEditIcon={hideEditIcon}
          hideDeleteIcon={hideDeleteIcon}
          hideAddIcon
          showToggle={showToggle}
          handleEditDialog={handleEditSubFeatureDialog}
          handleSwitch={handleSwitch}
        />
      ),
    },
  ];

  const columns = [
    {
      Header: " ",
      Cell: ({ row }) =>
        !!row?.original?.children?.length && (
          <ExpandCell
            row={row}
            rowIndex={row.index}
            expandedRows={expandedRows}
            handleToggleExpand={handleToggleExpand}
          />
        ),
    },
    {
      accessor: "name",
      Header: "Name",
    },
    {
      accessor: "description",
      Header: "Description",
    },
    {
      accessor: "createdOn",
      Header: "Created On",
    },
    {
      Header: "Action",
      Cell: ({ row }) => (
        <ActionCell
          row={row}
          hideEditIcon={hideEditIcon}
          hideDeleteIcon={hideDeleteIcon}
          hideAddIcon={hideAddIcon}
          showToggle={showToggle}
          handleEditDialog={handleEditFeatureDialog}
          handleAddDialog={handleAddSubFeatureDialog}
          handleSwitch={handleSwitch}
        />
      ),
    },
  ];
  const dispatch = useDispatch();
  const [features, setFeatures] = useState([]);

  const featuresList = useSelector((state) => state.featureDetails);
  const featuresListByTenant = useSelector(
    (state) => state.featureListByTenant
  );
  useEffect(() => {
    getFeaturesDetailsData();
    // Load tenant-specific features if tenantId is provided
    if (tenantId) {
      dispatch(getFeaturesListByTenant({ id: tenantId }));
    }
  }, [tenantId, dispatch]);

  const getFeaturesDetailsData = () => {
    dispatch(getFeatureDetails());
  };

  useEffect(() => {
    if (featuresList?.data?.length) {
      const tenantFeatureMap = featuresListByTenant?.data?.length
        ? featuresListByTenant?.data?.reduce((acc, item) => {
            acc[item.featureId] = item.isEnabled;
            return acc;
          }, {})
        : {};

      const filteredData = featuresList?.data?.map((row) => {
        const updatedChildren =
          row.children?.map((child) => ({
            ...child,
            isEnabled: tenantFeatureMap?.[child.uuid] ?? false,
          })) || [];
        return {
          ...row,
          createdOn: row?.createdAt
            ? moment(row.createdAt).format("DD-MM-YYYY")
            : "",
          isEnabled: tenantFeatureMap?.[row.uuid] ?? false,
          children: updatedChildren,
        };
      });

      setFeatures(filteredData);
    }
  }, [featuresList, featuresListByTenant]);

  const handleToggleExpand = (row) => {
    setExpandedRowsData(
      row?.original.children?.map((x) => ({
        ...x,
        createdOn: x?.createdAt ? moment(x.createdAt).format("DD-MM-YYYY") : "",
      }))
    );
    setExpandedRows((prev) => ({
      [row.original.uuid]: !prev[row.original.uuid],
    }));
  };

  const handleUserEditDialog = (rowObj) => {
    setEditUserDialogIsOpen(true);
  };

  const handleAddFeature = () => {
    setOpenAddSubFeatureDialog(false);
    setSubRow({});
    setOpenAddFeatureDialog(true);
  };

  const handleSwitch = async (e, featureId) => {
    const isChecked = e.target.checked;
    const response = await request("/api/v1/tenant-status/update", {
      method: "PUT",
      body: { isEnabled: isChecked, tenantId },
      params: featureId,
      auth: true,
    });
    if (response.status) {
      toast.success(`Feature ${isChecked ? "enabled" : "disabled"} successfully!`);
      setFeatures(prevFeatures =>
        prevFeatures.map(feature => {
          if (feature.uuid === featureId) {
            return { ...feature, isEnabled: isChecked };
          }
          if (feature.children) {
            const updatedChildren = feature.children.map(child =>
              child.uuid === featureId ? { ...child, isEnabled: isChecked } : child
            );
            // If the toggled feature is a child, also refresh expandedRowsData
            if (updatedChildren !== feature.children) {
              setExpandedRowsData(prev =>
                prev.map(child =>
                  child.uuid === featureId ? { ...child, isEnabled: isChecked } : child
                )
              );
            }
            return { ...feature, children: updatedChildren };
          }
          return feature;
        })
      );
    }
  };

  const formikAddFeature = useFormik({
    initialValues: {
      name: subRow ? subRow?.original?.name : "",
      description: subRow ? subRow?.original?.description : "",
    },
    validationSchema: validationSchemaAddFeature,
    onSubmit: async (values) => {
      let response;
      if (Object.keys(subRow).length) {
        response = await request("/api/v1/features/update", {
          method: "PUT",
          body: values,
          params: subRow?.original?.uuid,
          auth: true,
        });
      } else {
        response = await request("/api/v1/features/create", {
          method: "POST",
          body: values,
          auth: true,
        });
      }

      if (response.status) {
        const successMessage = Object.keys(subRow).length
          ? "Feature updated successfully!"
          : "Feature added successfully!";
        toast.success(successMessage);
        // refreshPagination();
        // handleBack();
        handleCloseFormDialog();
        formikAddFeature.resetForm();
        getFeaturesDetailsData();
      } else {
        toast.error(
          response?.error?.message || "Operation failed. Please try again."
        );
      }
    },
    enableReinitialize: true,
  });

  const formikAddSubFeature = useFormik({
    initialValues: {
      name: subFeatRow?.original?.name || "",
      description: subFeatRow?.original?.description || "",
    },
    validationSchema: validationSchemaAddFeature,
    enableReinitialize: true,
    onSubmit: async (values) => {
      let response;
      const modifiedValues = {
        ...values,
        parentId: subFeatRow?.original?.uuid,
      };
      if (Object.keys(subFeatRow).length) {
        response = await request("/api/v1/features/update", {
          method: "PUT",
          body: values,
          params: subFeatRow?.original?.uuid,
          auth: true,
        });
      } else {
        response = await request("/api/v1/features/create", {
          method: "POST",
          body: modifiedValues,
          auth: true,
        });
      }

      if (response.status) {
        const successMessage = Object.keys(subFeatRow).length
          ? "Feature updated successfully!"
          : "Feature added successfully!";
        toast.success(successMessage);
        handleCloseFormDialogSubFeature();
        formikAddFeature.resetForm();
        getFeaturesDetailsData();
        setExpandedRows((prev) => ({
          [subFeatRow.original.uuid]: !prev[subFeatRow.original.uuid],
        }));
      } else {
        toast.error(
          response?.error?.message || "Operation failed. Please try again."
        );
      }
    },
  });

  const handleCloseFormDialog = () => {
    setOpenAddFeatureDialog(false);
  };

  const handleCloseFormDialogSubFeature = () => {
    setOpenAddSubFeatureDialog(false);
  };
  return (
    <Box
      sx={{
        width: "100%",
        padding: "10px",
        cursor: "pointer",
        marginTop: "65px",
      }}
    >
      <EnterpriseTableComponent
        data={features || []}
        columns={columns}
        totalCount={features?.length}
        handleAddEnterPrise={handleAddFeature}
        expandedRows={expandedRows}
        handleUserEditDialog={handleUserEditDialog}
        expandedRowsData={expandedRowsData}
        columnsExpandableTable={columnsExpandableTable}
        hideFeatureButton={hideFeatureButton}
        hideEditIcon={hideEditIcon}
        hideDeleteIcon={hideDeleteIcon}
        hideAddIcon={hideAddIcon}
      />
      <Dialog
        isOpen={openAddFeatureDialog}
        cancelSubmitHandle={() => {
          formikAddFeature.resetForm();
          handleCloseFormDialog();
        }}
        handleClose={() => {
          formikAddFeature.resetForm();
          handleCloseFormDialog();
        }}
        okSubmitHandle={formikAddFeature.handleSubmit}
        okBtnText={Object.keys(subRow).length ? "Update" : "Submit"}
        title={
          <Typography variant="h4">
            {Object.keys(subRow).length ? "Edit Feature" : "Add Feature"}
          </Typography>
        }
      >
        <form onSubmit={formikAddFeature.handleSubmit}>
          <FormContent container spacing={3}>
            <Grid item xs={12}>
              <FormTextField
                placeholder={"Enter Feature Name"}
                onBlur={formikAddFeature.handleBlur}
                name="name"
                value={formikAddFeature.values.name}
                onChange={formikAddFeature.handleChange}
                label={"Name"}
                errorMsg={
                  formikAddFeature.touched.name && formikAddFeature.errors.name
                }
                focused={true}
              />
            </Grid>
            <Grid item xs={12}>
              <FormTextField
                placeholder={"Enter Feature Description"}
                onBlur={formikAddFeature.handleBlur}
                name="description"
                value={formikAddFeature.values.description}
                onChange={formikAddFeature.handleChange}
                label={"Description"}
                errorMsg={
                  formikAddFeature.touched.description &&
                  formikAddFeature.errors.description
                }
                focused={true}
              />
            </Grid>
          </FormContent>
        </form>
      </Dialog>
      <Dialog
        isOpen={openAddSubFeatureDialog}
        cancelSubmitHandle={() => {
          formikAddSubFeature.resetForm();
          handleCloseFormDialogSubFeature();
        }}
        handleClose={() => {
          formikAddSubFeature.resetForm();
          handleCloseFormDialogSubFeature();
        }}
        okSubmitHandle={formikAddSubFeature.handleSubmit}
        subFeatRow
        okBtnText={Object.keys(subFeatRow).length ? "Update" : "Submit"}
        title={
          <Typography variant="h4">
            {Object.keys(subFeatRow).length
              ? "Edit Sub Feature"
              : "Add Sub Feature"}
          </Typography>
        }
      >
        <form onSubmit={formikAddSubFeature.handleSubmit}>
          <FormContent container spacing={3}>
            <Grid item xs={12}>
              <FormTextField
                placeholder={"Enter Feature Name"}
                onBlur={formikAddSubFeature.handleBlur}
                name="name"
                value={formikAddSubFeature.values.name}
                onChange={formikAddSubFeature.handleChange}
                label={"Name"}
                errorMsg={
                  formikAddSubFeature.touched.name &&
                  formikAddSubFeature.errors.name
                }
                focused={true}
              />
            </Grid>
            <Grid item xs={12}>
              <FormTextField
                placeholder={"Enter Feature Description"}
                onBlur={formikAddSubFeature.handleBlur}
                name="description"
                value={formikAddSubFeature.values.description}
                onChange={formikAddSubFeature.handleChange}
                label={"Description"}
                errorMsg={
                  formikAddSubFeature.touched.description &&
                  formikAddSubFeature.errors.description
                }
                focused={true}
              />
            </Grid>
          </FormContent>
        </form>
      </Dialog>
    </Box>
  );
};

export default Features;
